import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import validator from "validator";
import ProfileSectionCard from "./ProfileSectionCard";
import UserService from "services/user.service";
import AuthService from "services/auth.service";

const EmailRecipients = ({ profile, onProfileRefresh, isReadOnly = false }) => {
	const MAX_RECIPIENTS_EMAILS = 3;
  const [emails, setEmails] = useState([]);

  const resetForm = () => {
    const currentEmails = profile?.recipients_emails || [];
    const formatted = currentEmails.length > 0
      ? currentEmails.map(item => ({ email: item.email || item }))
      : [{ email: "" }];
    setEmails(formatted);
  }

  useEffect(() => {
    resetForm();
    }, [profile]
  );

  const validateInput = () => {
    let isValid = true;

    // Check for empty fields and invalid formats
    for (let i = 0; i <emails.length; i++){
      if (!emails[i].email){
        toast.warning(`Email ${i + 1} cannot be empty!`)
        return false;
      }
      if (!validator.isEmail(emails[i].email)){
        toast.warning(`Email ${i + 1} has an invalid format.`)
        return false;
      }
    }

    // Check for duplicates
    const cleanedValues = emails.map(item => item.email.trim().toLowerCase());
    const hasDuplicates = cleanedValues.some((email, idx) => cleanedValues.indexOf(email) !== idx);

    if (hasDuplicates && isValid){
       toast.warning("Duplicate emails detected.");
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!validateInput()){
      throw new Error("Validation failed");
    }

    // Get original emails loaded from props
    const originalEmails = (profile?.recipients_emails || []).map(item => item.email || item);

    // Get current emails from form state
    const currentEmails = emails
      .filter(item => item.email.trim() !== "")
      .map(item => item.email.trim());

    // Find deleted emails (Emails that were in originalEmails but not in currentEmails)
    const deletedEmails = originalEmails
      .filter(email => !currentEmails.includes(email))
      .map(email => ({ email: email, updated: false}));

    // Find new emails (Emails that were not in originalEmails but in currentEmails)
    const newEmails = currentEmails
      .filter(email => !originalEmails.includes(email))
      .map(email => ({ email: email, updated: true}));

    const payloadData = [...deletedEmails, ...newEmails];

    if (payloadData.length === 0) return;

    try {
      await UserService.updateRecipientsEmails(payloadData);
      toast.success("Email recipients updated successfully!");
      if (onProfileRefresh) {
        await onProfileRefresh();
      }
    } catch (error) {
      if (error.response?.status === 401){
        AuthService.logout();
        window.location.replace("/eb/login");
      } else {
        toast.error(error.response?.data?.errors?.join(',') || "Something went wrong! Please try again later.")
      }
      throw error; // Prevents card from closing edit mode
    }
  };

  const handleEmailChange = (index, value) => {
    const updated = [...emails];
    updated[index].email = value;
    setEmails(updated);
  };

  const handleAddEmail = () => {
    if (emails.length < MAX_RECIPIENTS_EMAILS){
      setEmails([...emails, { email: ''}]);
    } else {
      toast.warning(`You can only add up to 3 email recipients.`);
    }
  };

  const handleDeleteEmail = (index) => {
    const updated = emails.filter((_, i) => i !== index);
    setEmails(updated);
  };

	return (
    <ProfileSectionCard
      title="Email Recipients"
      showEditButton={!isReadOnly}
      onSave={handleSave}
      onCancel={resetForm}
    >
      {({ isEditing }) => (
        <div className="profile-info-group">
          {/* READ-ONLY MODE */}
          {!isEditing && (
            <div className="profile-info-row">
              <h6 className="profile-info-label">Email</h6>
              <div className="profile-info-values">
                {emails.length > 0 && emails[0].email ? (
                  emails.map((item, index) => (
                    <ul className="profile-info-value" key={index}>
                      <li>{item.email}</li>
                    </ul>
                  ))
                ) : (
                  <p className="profile-info-value">-</p>
                )}
              </div>
            </div>
          )}

          {/* EDIT MODE */}
          {isEditing && (
            <div className="profile-info-row align-items-start">
              <h6 className="profile-info-label pt-2">Email</h6>
              <div className="flex-grow-1">
                {emails.map((item, index) => (
                  <div className="d-flex align-items-center mb-3 input-wrapper" key={index}>
                    <input
                      type="text"
                      value={item.email}
                      onChange={(e) => handleEmailChange(index, e.target.value)}
                      className="form-control profile-info-value py-6 px-4 w-100"
                      placeholder={`Email ${index + 1}`}
                      autoFocus={index === emails.length - 1}
                    />
                    {emails.length > 1 && (
                      <button
                        type="button"
                        className="delete-btn"
                        onClick={() => handleDeleteEmail(index)}
                      >
                        &times;
                      </button>
                    )}
                  </div>
                ))}
                
                {emails.length < MAX_RECIPIENTS_EMAILS && (
                  <button
                    type="button"
                    className="add-btn"
                    onClick={handleAddEmail}
                  >
                    + Add Email
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </ProfileSectionCard>
  
	);
};

export default EmailRecipients